import { cn } from "@shadcn/lib/utils";
import { NumberStepper } from "@shadcn/pro-blocks/application/buttons/button-1";
import { Button } from "@shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shadcn/ui/card";
import { Link, useSearch } from "@tanstack/react-router";
import { formatToCurrency } from "@utils/currency";
import { Plus } from "lucide-react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { useCart } from "~/contexts/CartContext";
import { SncProductCardFragment$key } from "./__generated__/SncProductCardFragment.graphql";

const fragment = graphql`
	fragment SncProductCardFragment on SncProduct {
		id
		priceAmount
		stock
		active
		product {
			id
			name
			description
			photo {
				id
				variant(variant: PUBLIC)
			}
			brand {
				id
				name
			}
			category {
				id
				name
				i18N
			}
		}
	}
`;

type SncProductCardProps = {
	product: SncProductCardFragment$key;
	showAddButton?: boolean;
	onProductClick?: (sncProductId: string) => void;
};

export const SncProductCard = (props: SncProductCardProps) => {
	const { showAddButton = true, onProductClick } = props;
	const { id, product, priceAmount, stock } = useFragment(fragment, props.product);
	const useCartStore = useCart();

	const { sncProductId, orgId, category } = useSearch({ strict: false }) as { sncProductId?: string; orgId?: string; category?: string };

	const addToCart = useCartStore((state) => state.addToCart);
	const updateQuantity = useCartStore((state) => state.updateQuantity);
	const cartItem = useCartStore((state) => state.items.find((item) => item.id === id));

	const outOfStock = stock === 0;

	const cardContent = (
		<Card
			className={cn("pt-0 overflow-hidden gap-4 border-none shadow-none", {
				"opacity-50": outOfStock,
			})}
		>
			<CardContent className="px-0 relative">
				<div className="relative">
					<img
						src={product.photo?.variant ?? "https://ui.shadcn.com/placeholder.svg"}
						alt={product.name}
						className="object-cover w-full h-48 rounded-xl"
					/>
					{outOfStock && (
						<div className="absolute inset-0 bg-gray-100/30 rounded-xl flex items-center justify-center">
							<span className="bg-white/90 px-3 py-1 rounded-md text-sm font-medium text-gray-600">Out of Stock</span>
						</div>
					)}
				</div>
			</CardContent>
			<CardHeader className="px-2">
				<div className="flex flex-col gap-1.5 min-h-20">
					<CardTitle className="font-breit line-clamp-1">{product.name}</CardTitle>
					<CardDescription className="line-clamp-2 min-h-10">{product.description}</CardDescription>
				</div>
			</CardHeader>
			<CardContent className="px-2 flex items-center justify-between">
				<p className={"font-breit font-semibold"}>{formatToCurrency(priceAmount)}</p>
				{showAddButton && (
					<div onClick={(e) => e.preventDefault()} onPointerDown={(e) => e.stopPropagation()}>
						{cartItem ? (
							<NumberStepper
								value={cartItem.quantity}
								onChange={(newQuantity) => {
									updateQuantity(id, newQuantity);
								}}
								min={0}
								max={stock}
							/>
						) : (
							<Button variant="outline" size="icon" onClick={() => addToCart(id, priceAmount)} disabled={outOfStock}>
								<Plus className="h-4 w-4" />
							</Button>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);

	if (onProductClick) {
		return (
			<div onClick={() => onProductClick(id)} className="cursor-pointer">
				{cardContent}
			</div>
		);
	}

	return (
		<Link from="/$hubMetaName/shop/" search={{ sncProductId: id, orgId, category }} replace={sncProductId !== undefined}>
			{cardContent}
		</Link>
	);
};
